import { useEffect, useRef } from "react";
import { Navigate, useParams } from "react-router-dom";
import moment from "moment-timezone";
import { useCurrentMeeting } from "@platform/hooks/useCurrentMeeting";
import {
  usePlatformStore,
  useVideoMeetingDetails,
  useVideoMeetingAccess,
  useVideoSession,
  useVideoConfig,
} from "@platform/store";
import { useDevices } from "@platform/features/video/providers/DevicesProvider";
import {
  useFetchVideoMeetingDetails,
  useVerifyVideoMeetingAccess,
} from "@core/features/videoMeeting";
import { useJoinVideoMeeting } from "@platform/features/videoMeeting";
import {
  VideoMeetingJoinContainer,
  VideoMeetingLoading,
  VideoMeetingAccessError,
} from "@platform/features/videoMeeting";
import { getNearestMediaRegion } from "@core/features/video";
import {
  ACTION_STATUS,
  ACTION_TYPE_SLUG,
  useUpdateActionStatus,
} from "@core/features/actions";
import { useMeetingRelatedUserPhaseAction } from "@platform/features/video/hooks/useMeetingRelatedUserPhaseAction";

/**
 * Video join page — device setup and meeting details before joining.
 * Reads meetingId from URL params first, falls back to useCurrentMeeting.
 */
export function VideoJoinPage() {
  const { meetingId: meetingIdFromUrl } = useParams();
  const { currentMeeting, isLoading: isLoadingCurrentMeeting } =
    useCurrentMeeting();
  const { isDevicesLoading } = useDevices();
  const user = usePlatformStore((s) => s.user);

  const { meetingId, meetingDetails, setMeetingId, setRoomId, setChimeExternalUserId, setMeetingDetails } =
    useVideoMeetingDetails();

  const {
    roomAccess,
    isVerifying: isVerifyingRoomAccess,
    setRoomAccess,
  } = useVideoMeetingAccess();

  const setRoomMembersData = usePlatformStore((s) => s.setRoomMembersData);

  const { setInitialized, setSessionError, setUserName, sessionError } =
    useVideoSession();

  const { region, setRegion, setMeetingMode, ...configRest } = useVideoConfig();

  const resetVideo = usePlatformStore((s) => s.resetVideo);
  const resetVideoMeeting = usePlatformStore((s) => s.resetVideoMeeting);

  // Resolve meetingId: URL param first, then useCurrentMeeting fallback
  const resolvedMeetingId = meetingIdFromUrl
    ? Number(meetingIdFromUrl)
    : currentMeeting?.id ?? null;

  // Sync resolved meetingId to store
  useEffect(() => {
    if (resolvedMeetingId && meetingId !== resolvedMeetingId) {
      setMeetingId(resolvedMeetingId);
    }
  }, [resolvedMeetingId, meetingId, setMeetingId]);

  // Fetch meeting details via React Query
  const {
    data: fetchedDetails,
    isLoading: isFetchingDetails,
    error: fetchError,
  } = useFetchVideoMeetingDetails({
    meetingId: resolvedMeetingId,
    user,
    isEnabled: !!resolvedMeetingId && !meetingDetails,
  });

  // Sync fetched details to store
  useEffect(() => {
    if (fetchedDetails && !meetingDetails) {
      setMeetingDetails(fetchedDetails);
    }
  }, [fetchedDetails, meetingDetails, setMeetingDetails]);

  // Verify room access once we have meeting details
  const videoRoomId = meetingDetails?.video_rooms?.[0]?.id ?? null;
  const {
    data: accessData,
    isLoading: isVerifyingAccess,
    error: accessError,
  } = useVerifyVideoMeetingAccess({
    roomId: videoRoomId,
    user,
    isEnabled: !!videoRoomId && !roomAccess,
  });

  // Sync access data to store
  useEffect(() => {
    if (accessData && !roomAccess) {
      setRoomAccess(accessData);
    }
  }, [accessData, roomAccess, setRoomAccess]);

  const { joinMeeting, clearExistingSession } = useJoinVideoMeeting({
    meetingId: meetingId?.toString() ?? null,
    roomId: roomAccess?.room_id ?? null,
    meetingDetails,
    region,
    enableSimulcast: configRest.enableSimulcast,
    keepLastFrameWhenPaused: configRest.keepLastFrameWhenPaused,
    isEchoReductionEnabled: configRest.isEchoReductionEnabled,
    setMeetingId: (id) => setMeetingId(id ? Number(id) : null),
    setRoomId,
    setInitialized,
    setRegion,
    setMeetingMode,
    setSessionError,
    setRoomMembersData,
    setChimeExternalUserId,
    setUserName,
    resetVideo,
    resetVideoMeeting,
  });

  // Resolve the matching meeting_attend / meeting_standalone action so we can
  // mark it IN_PROGRESS on arrival and COMPLETED once the user successfully joins.
  const meetingIdStr = resolvedMeetingId?.toString() ?? null;
  const actionProgramId = meetingDetails?.program ?? 0;

  const meetingAttendAction = useMeetingRelatedUserPhaseAction(
    meetingIdStr,
    ACTION_TYPE_SLUG.MEETING,
    actionProgramId
  );
  const meetingStandaloneAction = useMeetingRelatedUserPhaseAction(
    meetingIdStr,
    ACTION_TYPE_SLUG.MEETING_STANDALONE,
    actionProgramId
  );
  const attendAction = meetingAttendAction ?? meetingStandaloneAction;

  const { mutate: updateAttendStatus } = useUpdateActionStatus({
    actionId: attendAction?.id ?? 0,
    status: ACTION_STATUS.IN_PROGRESS,
    currentStatus: attendAction?.current_status?.name,
  });

  // Fire IN_PROGRESS once per mount, as soon as the action resolves. The
  // shouldUpdateStatus guard makes this a no-op when already IN_PROGRESS or
  // COMPLETED, so repeated visits to the page are safe.
  const hasMarkedInProgressRef = useRef(false);
  useEffect(() => {
    if (attendAction && !hasMarkedInProgressRef.current) {
      hasMarkedInProgressRef.current = true;
      updateAttendStatus();
    }
  }, [attendAction, updateAttendStatus]);

  const handleJoin = async () => {
    await joinMeeting();
    if (attendAction) {
      updateAttendStatus({ status: ACTION_STATUS.COMPLETED });
    }
  };

  // Clear existing session on mount
  useEffect(() => {
    clearExistingSession();
  }, []);

  // Detect nearest media region on mount
  useEffect(() => {
    getNearestMediaRegion().then(setRegion);
  }, [setRegion]);

  // Loading: fetching meeting details or verifying room access
  if (
    isFetchingDetails ||
    isVerifyingAccess ||
    isVerifyingRoomAccess ||
    (!meetingIdFromUrl && isLoadingCurrentMeeting)
  ) {
    return <VideoMeetingLoading />;
  }

  // Error: failed to fetch meeting details
  if (fetchError) {
    return <VideoMeetingAccessError errorMessage={fetchError.message} />;
  }

  // Error: failed to verify room access
  if (accessError) {
    return <VideoMeetingAccessError errorMessage={accessError.message} />;
  }

  if (sessionError) {
    return <VideoMeetingAccessError errorMessage={sessionError} />;
  }

  if (!resolvedMeetingId) {
    return <Navigate to="/dashboard" replace />;
  }

  if (meetingDetails && roomAccess) {
    return (
      <VideoMeetingJoinContainer
        meetingDetails={meetingDetails}
        isDevicesLoading={isDevicesLoading}
        meetingStartTime={
          meetingDetails.start_time
            ? moment
                .utc(meetingDetails.start_time)
                .tz(moment.tz.guess())
                .format("dddd, MMMM D [at] h:mma z")
            : undefined
        }
        onJoin={handleJoin}
      />
    );
  }

  return <VideoMeetingLoading />;
}

export default VideoJoinPage;
